import { Button, CloseXButton, ModalTemplate } from "@atomic";
import { Colors, Spacing } from "@constants";
import { htmlStringToPdfDownload, TestIDs } from "@utils";
import { I18n } from "react-redux-i18n";
import { Pressable, StyleSheet, TextStyle, View, ViewStyle } from "react-native";
import React, { useMemo, useState } from "react";
import { BrandTheme, CashSystem } from "@foodi/core";
import { useDispatch, useSelector } from "react-redux";
import { GlobalState } from "@redux";
import * as Sentry from "@sentry/react";
import moment from "moment";
import { CreditCard, DocumentDownload, ReceiptSVG, Download, Link } from "@assets";
import { OrderViewModel } from "@modules";
import style from "styled-components/native";
import { useMediaQuery } from "react-responsive";
import { Receipt } from "@stylesheets";

interface IProps {
  order: any;
  brandTheme: any;
  isMobile: any;
  idCashSystem?: string;
  cancellationButtons?: boolean;
  seeReceiptTitle: string;
  downloadReceiptTitle: string;
  seeReceiptTextStyle?: TextStyle;
  seeReceiptContainerStyle?: ViewStyle;
  downloadTextStyle?: TextStyle;
  downloadContainerStyle?: ViewStyle;
}

interface IPropsCancellationButtons {
  order: any;
  brandTheme: any;
  isMobile: any;
  idCashSystem?: string;
  seeReceiptTitle: string;
  downloadReceiptTitle: string;
  totalPrice: string;
}

export const CancellationReceiptButtons: React.FC<IPropsCancellationButtons> = ({
  order,
  brandTheme,
  isMobile,
  idCashSystem,
  seeReceiptTitle,
  downloadReceiptTitle,
  totalPrice
}) => {
  const isScreenBelow660 = useMediaQuery({ query: "(max-width:660px)" });
  const styles = useMemo(() => _styles(isMobile, brandTheme, isScreenBelow660), [isMobile, brandTheme, isScreenBelow660]);

  const isCSI = idCashSystem === CashSystem.CSI;

  return <BlockContainer isMobile={isMobile}>
    {!isCSI &&
      <>
        <View style={styles.cancellationTitleContainer}>
          <View style={styles.redLink}><Link width={30} height={30} color={Colors.redAttention}/></View>
          <BlockTitle style={styles.leftPadding} color={Colors.redAttention}>{I18n.t("confirmationPage.receipt.cancelReceiptSectionTitle")}</BlockTitle>
        </View>
        <Message style={styles.cancelMessage} color={Colors.redAttention}>{I18n.t("confirmationPage.receipt.cancelReceiptDescription")}</Message>
        <ReceiptButtons
          order={order}
          brandTheme={brandTheme}
          isMobile={isMobile}
          idCashSystem={idCashSystem}
          cancellationButtons={true}
          seeReceiptTitle={seeReceiptTitle}
          downloadReceiptTitle={downloadReceiptTitle}
          seeReceiptTextStyle={{
            color: '#000'
          }}
          seeReceiptContainerStyle={{
            backgroundColor: '#FFF',
            borderColor: '#e2e0e0',
          }}
          downloadTextStyle={{
            color: '#FFF'
          }}
          downloadContainerStyle={{
            backgroundColor: Colors.redAttention
          }}
        />
      </>
    }
    <View style={[
      !isCSI && styles.refundMessageSectionLeftBorder,
      styles.refundMessageSection
    ]}>
      <View style={styles.refundTitleContainer}>
        <span><CreditCard height={22} width={22} /></span>
        <Message style={styles.leftPadding} color={Colors.redAttention}>{I18n.t("confirmationPage.receipt.refundTitle")}</Message>
      </View>
      <Message color={Colors.redAttention}>{I18n.t("confirmationPage.receipt.refundMessage", { amount: totalPrice })}</Message>
    </View>
  </BlockContainer>;
}

export const ReceiptButtons: React.FC<IProps> = ({
  order,
  brandTheme,
  isMobile,
  idCashSystem,
  cancellationButtons,
  seeReceiptTitle,
  downloadReceiptTitle,
  seeReceiptTextStyle,
  seeReceiptContainerStyle,
  downloadTextStyle,
  downloadContainerStyle
}) => {
  const dispatch = useDispatch();
  const isScreenBelow660 = useMediaQuery({ query: "(max-width:660px)" });
  const styles = useMemo(() => _styles(isMobile, brandTheme, isScreenBelow660), [isMobile, brandTheme, isScreenBelow660]);
  const { id: idUser, idHoldingView, idGuest } = useSelector((state: GlobalState) => state.auth?.userInfo || {});
  const [receipt, setReceipt] = useState<string | null>(null);
  const [showReceipt, setShowReceipt] = useState<boolean>(false);
  const [loadingViewReceipt, setLoadingViewReceipt] = useState<boolean>(false);
  const [loadingDownloadReceipt, setLoadingDownloadReceipt] = useState<boolean>(false);
  const orderVM = new OrderViewModel(dispatch, "");
  const isButtonDisabled = loadingViewReceipt || loadingDownloadReceipt;

  const handleCloseReceipt = () => {
    setShowReceipt(false);
  };

  const handlePdfReceiptDownload = async () => {
    setLoadingDownloadReceipt(true);
    try {
      let receiptHtml = receipt;
      if (!receipt) {
        receiptHtml = await orderVM.getOrderHtmlReceipt({ idOrder: order?.id, canceled: cancellationButtons });
        setReceipt(receiptHtml);
      }
      await htmlStringToPdfDownload(
        receiptHtml!,
        `${I18n.t("refill.receiptCopy")}_${moment(new Date(order.created)).format("DD-MM-yyyy_HH-mm")}`,
        isMobile
      );
    }
    catch (error) {
      Sentry.captureException(error, {
        tags: { request: 'GET_HTML_ORDER_RECEIPT', },
        extra: {
          idOrder: order?.id,
          idCashSystem,
          idUser,
          idHoldingView,
          ...(idGuest ? { idGuest } : { nonBadgeUser: true }),
          context: 'Error while trying to view in-app / download an order receipt HTML-formatted, from the order details page'
        }
      });
    }
    finally {
      setLoadingDownloadReceipt(false);
    }
  }

  const handleViewReceipt = async () => {
    try{
      setLoadingViewReceipt(true);
      let receiptHtml = receipt;
      if (!receipt) {
        receiptHtml = await orderVM.getOrderHtmlReceipt({ idOrder: order?.id, canceled: cancellationButtons }); //todo
        setReceipt(receiptHtml);
      }
      setShowReceipt(true);
    }
    catch (error) {
      Sentry.captureException(error, {
        tags: { request: 'GET_HTML_ORDER_RECEIPT', },
        extra: {
          idOrder: order?.id,
          idCashSystem: idCashSystem,
          idUser,
          idHoldingView,
          ...(idGuest ? { idGuest } : { nonBadgeUser: true }),
          context: 'Error while trying to view in-app / download an order receipt HTML-formatted, from the order details page'
        }
      });
    }
    finally {
      setLoadingViewReceipt(false);
    }
  };

  return <>
    <View style={styles.receiptSection}>
      <Button
        styleButton={[styles.viewReceiptButton, seeReceiptContainerStyle]}
        styleLabel={{
          /* inline style to override specific inner label styles */
          color: brandTheme.buttonActionColors?.backgroundColor || Colors.foodiDefault,
          fontSize: 15,
          padding: '0px', /* necessary to be a string for the override to work */
          ...seeReceiptTextStyle
        }}
        loading={loadingViewReceipt}
        disabled={isButtonDisabled}
        loadingColor={brandTheme.buttonActionColors?.backgroundColor || Colors.foodiDefault}
        forwardTestID={TestIDs.components.Button.views.container}
        label={seeReceiptTitle}
        onPress={handleViewReceipt}
        icon={<ReceiptSVG width={20} height={20} />}
        styleIcon={styles.iconInButton}
      />
      <Button
        styleButton={[styles.downloadReceiptButton, downloadContainerStyle, isButtonDisabled && {
          backgroundColor: Colors.disabledBackground
        }]}
        /* inline style to override specific inner label styles */
        styleLabel={{
          fontSize: 15,
          padding: '0px', /* necessary to be a string for the override to work */
          ...downloadTextStyle
        }}
        loading={loadingDownloadReceipt}
        disabled={isButtonDisabled}
        forwardTestID={TestIDs.components.Button.views.container}
        label={downloadReceiptTitle}
        onPress={handlePdfReceiptDownload}
        icon={<DocumentDownload width={20} height={20} color={'#FFF'}/>}
        styleIcon={styles.iconInButton}
      />
    </View>
    {!!receipt && (
      <ModalTemplate
        isOpen={showReceipt}
        closeOnClickOutside
        handleClose={handleCloseReceipt}
        style={styles.receiptModalDiv}
        customComponentCloseButton={
          <>
            <CloseXButton
              closeXButtonStyle={styles.closeXButtonStyleReceiptModal}
              callback={handleCloseReceipt}
            />
            <Pressable
              style={styles.downLoadContainerModal}
              onPress={handlePdfReceiptDownload}
            >
              <Download />
            </Pressable>
          </>
        }
      >
        <Receipt style={styles.receipt} dangerouslySetInnerHTML={{ __html: receipt }} />
      </ModalTemplate>
    )}
  </>
}

const _styles = (isMobile: boolean, brandTheme: BrandTheme, isScreenBelow660: boolean) =>
  StyleSheet.create({
    loadingStyle: {
      position: "absolute",
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      alignItems: "center",
      justifyContent: "center",
    },
    closeXButtonStyle: {
      top: -15,
      right: -5,
    },
    closeXButtonStyleReceiptModal: {
      top: -25,
      right: -5,
    },
    comment: {
      marginVertical: Spacing.XS,
    },
    receiptSection: {
      width: '100%',
      flexDirection: isScreenBelow660 ? 'column' : 'row',
      justifyContent: 'space-between',
      marginTop: 10
    },
    viewReceiptButton: {
      backgroundColor: 'FFF',
      borderWidth: 2,
      borderStyle: 'solid',
      borderColor: brandTheme.buttonActionColors?.backgroundColor || Colors.foodiDefault,
      borderRadius: 8,
      width: isScreenBelow660 ? '100%' : '49%',
      height: 45
    },
    downloadReceiptButton: {
      width: isScreenBelow660 ? '100%' : '49%',
      height: 45,
      ...(isScreenBelow660 ? { marginTop: 10 } : {})
    },
    receiptModalDiv: {
      width: isScreenBelow660 ? "100%" : 520,
      maxHeight: isScreenBelow660 ? "100%" : "95vh",
    },
    downLoadContainerModal: {
      position: "absolute",
      right: 60,
      top: -30,
    },
    receipt: {
      marginTop: 10,
    },
    iconInButton: {
      paddingTop: 5,
      paddingRight: 6
    },
    leftPadding: {
      paddingLeft: 5
    },
    cancellationTitleContainer: {
      flexDirection: 'row',
      marginBottom: 15
    },
    redLink: {
      marginTop: 2
    },
    cancelMessage: {
      marginBottom: 20
    },
    refundMessageSection: {
      paddingLeft: 15,
      paddingBottom: 7
    },
    refundMessageSectionLeftBorder: {
      borderLeftColor: `${Colors.redAttention}80`,
      borderLeftWidth: 4,
      marginTop: 25,
      paddingTop: 7,
    },
    refundTitleContainer: {
      flexDirection: 'row'
    }
  });

export const BlockContainer = style.View<{ isMobile?: boolean }>`
    width: clamp(365px, 90vw, 750px);
    border: 2px solid ${Colors.redAttention}33;
    border-radius: 16px;
    padding: ${({ isMobile }) => (isMobile ? "12px 10px" : "12px 20px")};
    margin-top: 23px;
    background-color: #FF040008  /* last 2 characters are for transparency control */
`;

export const BlockTitle = style.Text<{ color?: string; hide?: boolean }>`
    color: ${({ color }) => (color ? color : Colors.foodiBlack)};
    font-family: manrope-bold;
    font-size: 16px;
    letter-spacing: 0;
    line-height: 30px;
    opacity: ${({ hide }) => (hide ? 0 : 1)};
`;
export const Message = style.Text<{ color?: string; hide?: boolean }>`
    color: ${({ color }) => (color ? color : Colors.foodiBlack)};
    font-family: manrope-bold;
    font-size: 14px;
    letter-spacing: 0;
    opacity: ${({ hide }) => (hide ? 0 : 1)};
`;
